import WineInfiniteTable from '../../components/WineAdditionComponents/WineInfiniteTable';
import ProducerInfiniteTable from '../../components/WineAdditionComponents/ProducerInfiniteTable';
import React from 'react';
import Badge from '../../components/Badge';
import RedSliderSwitch from '../../components/FormComponents/RedSliderSwitch';
import InputGroup from '../../components/FormElements/InputGroup';
import Button from '../../components/Buttons';
import { AddIcon, CloseIcon } from '../../assets/Icons';
import { Building2, Merge, Wine } from 'lucide-react';


interface DirectInputContentProps {
    // All props needed for the content
    newNarrowKeyword: string;
    setNewNarrowKeyword: (val: string) => void;
    handleKeywordKeyPress: (e: React.KeyboardEvent<HTMLInputElement>) => void;
    filter_id: string;
    handlefilter_idChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
    keywordsList: string[];
    removeKeyword: (index: number) => void;
    lockedProducerLiterals: string[];
    removeLockProducerLiteral: (index: number) => void;
    fetchedProducers: any[];
    hasMoreProducers: boolean;
    isLoadingProducers: boolean;
    isLoadingMoreProducers: boolean;
    handleEditProducer: (id: string) => void;
    handleAddWineToProducer: (producerId: string) => void;
    handleProducerIdClick: (id: string) => void;
    handleWordClick: (word: string) => void;
    onLoadMoreProducers: () => void;
    producersBadgeCount: number;
    search_partialProducer: boolean;
    setSearch_partialProducer: (val: boolean) => void;
    fetchedWines: any[];
    hasMoreWines: boolean;
    isLoadingWines: boolean;
    isLoadingMoreWines: boolean;
    handleWineRowSelect: (row: any, checked: boolean) => void;
    selectedWineRows: any[];
    onLoadMoreWines: () => void;
    winesBadgeCount: number;
    search_partial: boolean;
    setSearch_partial: (val: boolean) => void;
    handleMergeSelected: () => void;
    shouldShowEditWines?: boolean;
    handleEditWine?: (id: string) => void;
    fromExternalInput?: boolean;
    dataLoaded?: boolean;
    selectedLiteral?: { id: string; literal: string } | null;
    handleLiteralClick?: (id: string, literal: string) => void;
}

const DirectInputContent: React.FC<DirectInputContentProps> = (props) => {
    // Use selectedLiteral and handleLiteralClick from props (lifted state)
    const { selectedLiteral, handleLiteralClick } = props;

    return (
        <div id="directInputPage" className="border rounded-lg p-4">
            <div className="grid grid-cols-2 gap-8">
                {/* Left Grid - Producers Section */}
                <div className="flex flex-col h-[calc(100dvh-355px)] min-h-[500px]">
                    {/* Search Inputs */}
                    <div className="grid grid-cols-2 gap-4">
                        <InputGroup
                            type="text"
                            label="Keyword"
                            name="newNarrowKeyword"
                            value={props.newNarrowKeyword}
                            handleChange={e => props.setNewNarrowKeyword(e.target.value)}
                            onKeyPress={props.handleKeywordKeyPress}
                            inputVariant="floating"
                        />
                        <InputGroup
                            type="text"
                            label="ID"
                            name="filter_id"
                            value={props.filter_id}
                            handleChange={props.handlefilter_idChange}
                            inputVariant="floating"
                        />
                    </div>

                    {/* Keywords Display */}
                    <div className="mb-3 min-h-[32px] border border-gray-200 rounded bg-gray-50 p-2">
                        {props.keywordsList.length === 0 ? (
                            <div className="text-gray-400 text-sm">Add keywords to search...</div>
                        ) : (
                            <div className="flex flex-wrap gap-2">
                                {props.keywordsList.map((keyword, index) => (
                                    <div key={index} className="flex items-center bg-white border border-gray-300 rounded-full px-3 py-1 text-sm">
                                        <span>{keyword}</span>
                                        <button
                                            onClick={() => props.removeKeyword(index)}
                                            className="keywordSpans ml-2 text-gray-500 hover:text-red-500"
                                        >
                                            <CloseIcon className="w-4 h-4 text-red-500" />
                                        </button>
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>

                    <hr className='mb-3' />

                    {/* Producers Section Header */}
                    <div className="flex flex-col md:flex-row md:items-center md:justify-between mb-3 gap-2">
                        <div className="flex items-center gap-2">
                            <Building2 className="h-5 w-5" />
                            <span className="font-bold">Producers</span>
                            { props.producersBadgeCount > 0 && <Badge count={props.producersBadgeCount}/> }
                        </div>
                        <div className="flex items-center gap-2">
                            <label className="text-xs text-gray-600">Search partial word</label>
                            <RedSliderSwitch
                                checked={props.search_partialProducer}
                                onChange={props.setSearch_partialProducer}
                                label=""
                            />
                        </div>
                    </div>

                    {/* Locked Producers Display */}
                    <span className="text-gray-700 font-medium">Locked:</span>
                    <div className="mb-3 min-h-[32px] border border-gray-200 rounded bg-gray-50 p-2">
                        {props.lockedProducerLiterals.length === 0 ? (
                            <div className="text-gray-400 text-sm">Click on IDs below to search...</div>
                        ) : (
                            <div className="flex flex-wrap gap-2">
                                {props.lockedProducerLiterals.map((id, idx) => (
                                    <span key={idx} className="flex items-center bg-blue-600 text-white rounded-full px-3 py-1 text-sm">
                                        {id}
                                        <button
                                            className="producerSpans ml-2 text-white hover:text-gray-200 focus:outline-none"
                                            onClick={() => props.removeLockProducerLiteral(idx)}
                                            title="Remove"
                                        >
                                            <CloseIcon className="w-4 h-4" />
                                        </button>
                                    </span>
                                ))}
                            </div>
                        )}
                    </div>
                    
                    {/* Producers Infinite Table */}
                    <ProducerInfiniteTable
                        data={props.fetchedProducers}
                        hasMore={props.hasMoreProducers}
                        isLoading={props.isLoadingProducers}
                        isAppending={props.isLoadingMoreProducers}
                        handleEditProducer={props.handleEditProducer}
                        handleAddWineToProducer={props.handleAddWineToProducer}
                        handleProducerIdClick={props.handleProducerIdClick}
                        handleWordClick={props.handleWordClick}
                        onLoadMore={props.onLoadMoreProducers}
                        fromExternalInput={props.fromExternalInput}
                    />
                </div>

                {/* Right Grid - Wines Section */}
                <div className="flex flex-col h-[calc(100dvh-355px)] min-h-[500px]">
                    {/* Wines Section Header */}
                    <div className="mb-3 flex flex-col gap-2 md:flex-row md:items-center md:justify-between">
                        {/* Row 1: Icon, label, count */}
                        <div className="flex items-center gap-2">
                            <Wine className="h-5 w-5" />
                            <span className="font-bold">Wines</span>
                            { props.winesBadgeCount > 0 && <Badge count={props.winesBadgeCount} /> }
                        </div>
                        {/* Row 2: Search partial word and toggle */}
                        <div className="flex items-center gap-2 md:ml-4">
                            <label className="text-xs text-gray-600">Search partial word</label>
                            <RedSliderSwitch
                                checked={props.search_partial}
                                onChange={props.setSearch_partial}
                                label=""
                            />
                        </div>
                        {/* Row 3: Merge button */}
                        <div className="flex md:ml-4">
                            <Button
                                variant='dark'
                                size='md'
                                onClick={props.handleMergeSelected}
                                className={`${
                                    props.selectedWineRows.length < 1 || props.selectedWineRows.length > 3
                                        ? 'bg-gray-300 !text-black opacity-50 cursor-not-allowed'
                                        : ''
                                }`}
                                disabled={props.selectedWineRows.length < 1 || props.selectedWineRows.length > 3}
                            >
                                <div className="flex items-center gap-2">
                                    <Merge className="w-4 h-4" />
                                    <span>Merge</span>
                                </div>
                            </Button>
                        </div>
                    </div>

                    <WineInfiniteTable
                        data={props.fetchedWines}
                        hasMore={props.hasMoreWines}
                        isLoading={props.isLoadingWines}
                        isAppending={props.isLoadingMoreWines}
                        onRowSelect={props.handleWineRowSelect}
                        selectedWineRows={props.selectedWineRows}
                        handleWordClick={props.handleWordClick}
                        shouldShowEditWines={props.shouldShowEditWines}
                        onLoadMore={props.onLoadMoreWines}
                        handleEditWine={props.handleEditWine}
                        fromExternalInput={props.fromExternalInput}
                        dataLoaded={props.dataLoaded}
                        selectedLiteral={selectedLiteral}
                        handleLiteralClick={handleLiteralClick}
                    />
                </div>
            </div>
        </div>
    );
};

export default DirectInputContent;
